/**
 * Main Layout Component
 * 
 * Provides the sidebar layout for all authenticated pages.
 * Mobile-first responsive design with bottom navigation on mobile devices.
 */
import { useState, useEffect } from "react";
import { CarryoverDecisionGate } from '@/components/financial';
import { SidebarProvider } from "@/components/ui/sidebar";
import AppSidebar from "./sidebar/AppSidebar";
import { InitialSetupModal } from "./onboarding/InitialSetupModal";
import { FirstAccessTutorialModal } from "./onboarding/FirstAccessTutorialModal";
import { useAuth } from "@/contexts/auth.context";
import { useTheme } from "@/hooks/use-theme";
import { Outlet, useLocation } from "react-router-dom";
import { PendingPlanChangeModal, SubscriberProfileCompletionModal, SubscriptionGuard, SubscriptionWarningBanner, TrialNoticeBanner } from "@/components/subscription";
import { agentService, budgetPeriodService } from "@/services/financial.service";
import { MobileBottomNav } from "./navigation/MobileBottomNav";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";
import { useSubscriptionPaymentSync } from "@/hooks/use-subscription-payment-sync";
import { TrialWelcomeModal } from "./onboarding/TrialWelcomeModal";
import { TrialEndedModal } from "./onboarding/TrialEndedModal";
import { accountService } from "@/services/account.service";
import { shouldRequireSubscriberProfileModal } from "@/lib/subscriber-profile-completion";
import { isForbiddenError } from "@/lib/api-errors";
import {
  subscribeToFirstAccessTutorialLaunch,
  type TutorialLaunchMode,
} from "./onboarding/tutorial-runtime";
import { parseApiInstant } from '@/lib/brasiliaDate';

const SIDEBAR_STORAGE_KEY = "librattum-sidebar-state";

const SUBSCRIPTION_GUARD_BYPASS = ['/account', '/support'] as const;

export function Layout() {
  const { user, logout, dismissFirstAccessTutorial } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const isMobile = useIsMobile();
  
  // Load initial state from localStorage
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    return stored !== null ? stored === "true" : true;
  });
  const [showInitialSetup, setShowInitialSetup] = useState(false);
  const [showFirstAccessTutorial, setShowFirstAccessTutorial] = useState(false);
  const [showTrialWelcome, setShowTrialWelcome] = useState(false);
  const [profileCompletionDismissed, setProfileCompletionDismissed] = useState(false);
  const [showTrialEnded, setShowTrialEnded] = useState(false);
  const [showPendingPlanChange, setShowPendingPlanChange] = useState(false);
  const [tutorialStartMode, setTutorialStartMode] = useState<TutorialLaunchMode>("welcome");
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [setupChecked, setSetupChecked] = useState(false);
  const subscriptionStatus = useSubscriptionStatus();
  useSubscriptionPaymentSync(subscriptionStatus);
  const {
    subscription: liveSubscription,
    isLoading: subscriptionLoading,
    isBlocked,
  } = subscriptionStatus;
  const isTrial = liveSubscription?.status?.toLowerCase() === "trial";
  // Trial ATIVO (ainda não expirou): só aqui faz sentido o modal "X dias grátis".
  // Trial expirado é tratado pelo bloqueio global (SubscriptionGuard).
  const trialActive =
    isTrial &&
    !!liveSubscription?.trial_end &&
    (parseApiInstant(liveSubscription.trial_end)?.getTime() ?? 0) > Date.now();
  // "Complete seu cadastro" (CPF/endereço) só é exigido em plano PAGO. Durante o
  // trial e no plano gratuito o usuário entra e explora sem esse bloqueio — os
  // dados são coletados na hora de escolher/assinar um plano pago (checkout).
  // shouldRequireSubscriberProfileModal cobre pendências de cadastro E o self-heal
  // (requires_vio_provisioning: assinatura ativa não provisionada na VIO); as
  // guardas de trial/gratuito/loading mantêm a regra de só bloquear em plano pago.
  const isFreePlan = !!liveSubscription?.is_free_plan;
  // isBlocked entra aqui pelo mesmo motivo do InitialSetupModal: esta modal
  // também é não-descartável (sem X, sem Escape, sem clique fora — ver
  // SubscriberProfileCompletionModal.tsx) e seu gatilho (CPF/CNPJ ou endereço
  // incompletos) independe do estado da assinatura. Sem esta guarda, um
  // assinante terminal (liveSubscription null → !isTrial && !isFreePlan
  // sempre verdadeiros) com cadastro incompleto ficaria preso atrás dela,
  // reproduzindo o defeito original que esta branch existe para resolver.
  // O X fecha só neste acesso (chamado 7116): o estado não é guardado, então a
  // janela volta ao recarregar ou entrar de novo, até o cadastro ser concluído.
  const showSubscriberProfileCompletion =
    !profileCompletionDismissed &&
    shouldRequireSubscriberProfileModal(user) &&
    !isTrial &&
    !isFreePlan &&
    !subscriptionLoading &&
    !isBlocked;

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarOpen));
  }, [sidebarOpen]);

  // Verificar no backend se o usuário já completou a configuração inicial
  // A modal deve aparecer enquanto o usuário NÃO tiver AMBOS: agentes E período orçamentário
  useEffect(() => {
    if (!user || setupChecked || showSubscriberProfileCompletion) return;
    // Sob bloqueio de assinatura a verificação não faz sentido: todos os
    // endpoints financeiros respondem 403 e a tela correta é a de bloqueio.
    // Enquanto carrega, esperar — decidir com dado incompleto abre a modal
    // por engano.
    if (subscriptionLoading || isBlocked) return;

    const checkInitialSetup = async () => {
      const localKey = `onboarding-complete-${user.id}`;
      
      // Sempre verificar no backend - a fonte de verdade é o backend
      // O localStorage serve apenas para evitar flickering, mas nunca deve
      // impedir a exibição da modal se o backend indicar que há configuração pendente
      
      try {
        // Verificar se já existem agentes no backend
        const agents = await agentService.getAll();
        const hasAgents = agents && agents.length > 0;
        
        // Verificar se existe período orçamentário
        let hasPeriod = false;
        try {
          const period = await budgetPeriodService.getCurrent();
          hasPeriod = !!period;
        } catch {
          // Erro ao buscar período - considera que não tem
          hasPeriod = false;
        }
        
        // Mostrar modal se NÃO tiver agentes OU NÃO tiver período
        // A modal deve permanecer aberta até que ambos sejam configurados com sucesso
        const needsSetup = !hasAgents || !hasPeriod;
        
        console.log(`[InitialSetup] hasAgents: ${hasAgents}, hasPeriod: ${hasPeriod}, needsSetup: ${needsSetup}`);
        
        if (needsSetup) {
          // Limpa o localStorage para forçar re-verificação na próxima vez
          localStorage.removeItem(localKey);
          setShowInitialSetup(true);
        } else {
          // Tem agentes E período - marca como completo
          localStorage.setItem(localKey, "true");
          setShowInitialSetup(false);
        }
      } catch (error) {
        // 403 é o gate de plano dizendo "sem acesso", não "falta configurar".
        // Abrir a modal aqui prendia o assinante cancelado numa configuração
        // inicial que nunca conclui — a modal não fecha e o salvar erra 403.
        if (isForbiddenError(error)) {
          setShowInitialSetup(false);
        } else {
          // Falha transitória (rede/5xx): mostrar a modal é a escolha segura,
          // para não impedir quem realmente precisa configurar.
          console.error("Erro ao verificar configuração inicial:", error);
          localStorage.removeItem(localKey);
          setShowInitialSetup(true);
        }
      } finally {
        setCheckingSetup(false);
        setSetupChecked(true);
      }
    };

    checkInitialSetup();
  }, [user, setupChecked, showSubscriberProfileCompletion, subscriptionLoading, isBlocked]);

  useEffect(() => {
    if (!user || checkingSetup || showInitialSetup || showSubscriberProfileCompletion) return;
    setTutorialStartMode("welcome");
    setShowFirstAccessTutorial(Boolean(user.show_first_access_tutorial));
  }, [user, checkingSetup, showInitialSetup, showSubscriberProfileCompletion]);

  // Modal informativo "X dias grátis": uma vez por usuário, durante o trial,
  // após as configurações iniciais e antes do tutorial.
  useEffect(() => {
    if (!user || !trialActive || !setupChecked) return;
    if (showSubscriberProfileCompletion || showInitialSetup) return;
    if (localStorage.getItem(`trial-welcome-seen-${user.id}`) === "true") return;
    setShowTrialWelcome(true);
  }, [user, trialActive, setupChecked, showSubscriberProfileCompletion, showInitialSetup]);

  // Aviso não-bloqueante: o backend migrou o trial expirado para o gratuito
  // (on-access). Mostra uma vez por usuário, convidando ao upgrade.
  useEffect(() => {
    if (!user || !liveSubscription?.trial_just_ended) return;
    if (localStorage.getItem(`trial-ended-seen-${user.id}`) === "true") return;
    setShowTrialEnded(true);
  }, [user, liveSubscription?.trial_just_ended]);

  // Troca de plano aguardando pagamento: logo após o pedido ou quando o
  // usuário volta sem pagar. O modal reaparece a cada acesso enquanto a
  // pendência existir no backend (sem localStorage — o estado é do servidor) e
  // a cada novo pedido (`pending_requested_at` muda mesmo com o mesmo plano).
  useEffect(() => {
    if (!user || !liveSubscription?.pending_plan_id) {
      setShowPendingPlanChange(false);
      return;
    }
    setShowPendingPlanChange(true);
  }, [user, liveSubscription?.pending_plan_id, liveSubscription?.pending_requested_at]);

  useEffect(() => {
    return subscribeToFirstAccessTutorialLaunch((mode) => {
      setTutorialStartMode(mode);
      setShowFirstAccessTutorial(true);
    });
  }, []);

  useEffect(() => {
    if (showFirstAccessTutorial && !isMobile) {
      setSidebarOpen(true);
    }
  }, [showFirstAccessTutorial, isMobile]);

  // Suporte a navegação por hash (ex.: "#sheet") para abrir a "planilha" do módulo.
  // Faz scroll e tenta focar o primeiro input dentro do elemento.
  useEffect(() => {
    if (!location.hash) return;

    const targetId = location.hash.replace("#", "");
    if (!targetId) return;

    let cancelled = false;
    let attempts = 0;

    const tryScroll = () => {
      if (cancelled) return;

      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.querySelector("input")?.focus?.();
        return;
      }

      attempts += 1;
      if (attempts < 30) {
        requestAnimationFrame(tryScroll);
      }
    };

    tryScroll();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.hash]);

  return (
    <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen}>
      <div className="h-screen w-full bg-background flex flex-col overflow-hidden">
        {/* Warning banner for overdue invoices (1-5 days). Não renderiza sob
            bloqueio: para uma assinatura terminal (cancelada/inativa) pagar
            não reativa nada, então o CTA "Pagar agora" não pode aparecer
            acima da tela de bloqueio oferecendo uma saída que não existe. */}
        {!isBlocked && <SubscriptionWarningBanner />}

        <SubscriberProfileCompletionModal
          open={showSubscriberProfileCompletion}
          user={user}
          reason="access"
          onClose={() => setProfileCompletionDismissed(true)}
        />

        <InitialSetupModal
          open={showInitialSetup && !showSubscriberProfileCompletion && !isBlocked}
          defaultAgentName={user?.name}
          onSuccess={() => {
            // Configuração concluída com sucesso - salva no localStorage
            if (user?.id) {
              localStorage.setItem(`onboarding-complete-${user.id}`, "true");
            }
          }}
          onComplete={() => {
            if (user?.id) {
              localStorage.setItem(`onboarding-complete-${user.id}`, "true");
            }
            setShowInitialSetup(false);
            // Em trial, mostra o modal de "X dias grátis" antes do tutorial.
            const seenKey = user?.id ? `trial-welcome-seen-${user.id}` : null;
            if (trialActive && seenKey && localStorage.getItem(seenKey) !== "true") {
              setShowTrialWelcome(true);
            }
            setTutorialStartMode("welcome");
            setShowFirstAccessTutorial(Boolean(user?.show_first_access_tutorial));
          }}
        />

        <TrialWelcomeModal
          open={showTrialWelcome && !showSubscriberProfileCompletion && !showInitialSetup}
          trialEnd={liveSubscription?.trial_end ?? null}
          onClose={() => {
            if (user?.id) {
              localStorage.setItem(`trial-welcome-seen-${user.id}`, "true");
            }
            setShowTrialWelcome(false);
          }}
        />

        <TrialEndedModal
          open={showTrialEnded && !showSubscriberProfileCompletion && !showInitialSetup}
          onClose={() => {
            if (user?.id) localStorage.setItem(`trial-ended-seen-${user.id}`, "true");
            setShowTrialEnded(false);
            void accountService.ackTrialEnded().catch(() => {});
          }}
        />

        {liveSubscription?.pending_plan_id && (
          <PendingPlanChangeModal
            open={
              showPendingPlanChange &&
              !showSubscriberProfileCompletion &&
              !showInitialSetup &&
              !showTrialWelcome &&
              !showTrialEnded
            }
            subscription={liveSubscription}
            onClose={() => setShowPendingPlanChange(false)}
          />
        )}

        <FirstAccessTutorialModal
          open={showFirstAccessTutorial && !showSubscriberProfileCompletion && !showTrialWelcome}
          startMode={tutorialStartMode}
          userName={user?.name}
          onComplete={() => {
            setTutorialStartMode("welcome");
            setShowFirstAccessTutorial(false);
            if (user?.show_first_access_tutorial) {
              void dismissFirstAccessTutorial();
            }
          }}
        />
        
        <div className="flex-1 flex min-h-0">
          {/* Desktop Sidebar - hidden on mobile */}
          <AppSidebar
            onLogout={logout}
            darkMode={isDark}
            onToggleDarkMode={toggleTheme}
            user={user}
          />

          {/* Main content area with bottom padding for mobile nav */}
          <main className="flex-1 overflow-auto min-h-0 pb-16 md:pb-0">
            {/* Período de teste: situação e caminho para assinar, sem bloquear.
                Dentro do <main> para não ficar sob a barra lateral fixa. */}
            {!isBlocked && <TrialNoticeBanner subscription={liveSubscription} />}
            {/* SubscriptionGuard bloqueia rotas autenticadas quando a fatura
                está vencida acima do limite (`suspension_days_overdue` da VIO Pay).
                Rotas liberadas mesmo com assinatura bloqueada:
                - /account → onde o usuário paga ou troca de plano (senão os
                  botões da página bloqueada cairiam em loop).
                - /support → suporte e central de ajuda; o usuário pode precisar
                  tirar dúvidas antes de pagar. */}
            {SUBSCRIPTION_GUARD_BYPASS.some((prefix) => location.pathname.startsWith(prefix)) ? (
              <Outlet />
            ) : (
              <SubscriptionGuard>
                <Outlet />
              </SubscriptionGuard>
            )}
          </main>
        </div>

        {/* Pergunta sobre o saldo do PO anterior. Fica no layout porque a virada
            de período pode pegar o usuário em qualquer tela.
            Cede a vez aos modais de onboarding/assinatura: sendo não-dispensável,
            empilhar sobre eles esconderia a tela de pagamento sem saída. */}
        {!showSubscriberProfileCompletion &&
          !showInitialSetup &&
          !showTrialWelcome &&
          !showTrialEnded && <CarryoverDecisionGate />}

        {/* Mobile Bottom Navigation - only visible on mobile */}
        {isMobile && (
          <MobileBottomNav
            onLogout={logout}
            darkMode={isDark}
            onToggleDarkMode={toggleTheme}
            user={user}
          />
        )}
      </div>
    </SidebarProvider>
  );
}

export default Layout;
